import { routingStore } from 'app'
import { inject, observer } from 'mobx-react'
import { Clients } from 'pages/clients'
import { HomePage } from 'pages/homePage'
import React from 'react'
import { useLocation, Location } from 'react-router'
import { useParams } from 'react-router'
import { Navigate, Route, Routes as ReactRoutes, RouteProps, NavigateFunction, useNavigate } from 'react-router-dom'
import { PageContainer } from './components/pageContainer'
import {
  AdminLogin,
  BankIDLogin,
  Certificates,
  CoursePage,
  Courses,
  Customers,
  Editor,
  Email, Faq,
  ForgotPassword,
  Library,
  Packages, Programs,
  ResetPassword,
  Settings,
  Statistics,
  StyleGuide,
  SystemUsers, Users,
  VerifyAccount
} from './pages'
import { RoutingStore } from './stores'
import { Evaluations } from 'pages/evaluations'


export const base_URL: string = process.env.BASE_URL as string;

export type RoutingParams = Partial<Record<
    "id"
  | "subid"
  | "view"
  | "subview"
  | "token"
  , string>
>;

export enum Pages {
  Certificates   = 'certificates',
  Course         = 'course',
  Courses        = 'courses',
  Customers      = 'customers',
  CompanySelect  = 'company',
  Editor         = 'editor',
  Email          = 'email',
  Evaluations    = 'evaluations',
  Library        = 'library',
  BankID         = 'bankID',
  Login          = 'login',
  AdminLogin     = 'login',
  ForgotPassword = 'forgot-password',
  ResetPassword  = 'reset-password',
  Program        = 'program',
  Programs       = 'programs',
  Package        = 'package',
  Packages       = 'packages',
  Notes          = 'notes',
  Settings       = 'settings',
  Statistics     = 'analytics',
  Licenses       = 'licenses',
  StyleGuide     = 'style-guide',
  System         = 'system',
  Users          = 'all-users',
  Faq            = 'faq',
}

/**
 * Construct the routing path from a page name and routing params.
 * This can be used with routingStore.push().
 *
 * @param page Page name from the Page enumeration
 * @param params Object that contains :id, :view and :subview params
 */
export const getRoutePath = (page: Pages, params?: RoutingParams): string => {
  return (
    '/' +
    page +
    (params && params.id      ? '/' + params.id : '') +
    (params && params.view    ? '/' + params.view : '') +
    (params && params.subid   ? '/' + params.subid : '') +
    (params && params.subview ? '/' + params.subview : '') +
    (params && params.token   ? '/' + params.token : '')
  );
}

export const publicRoutes: (RouteProps & {component: React.FunctionComponent})[] = [
  {
    path:      '/',
    component: HomePage,
  },{
    path:      '/style-guide',
    component: StyleGuide,
  },{
    path:      '/bankID',
    component: BankIDLogin,
  },{
    path:      '/login',
    component: AdminLogin,
  },{
    path:      '/forgot-password',
    component: ForgotPassword,
  },{
    path:      '/reset-password/:token',
    component: ResetPassword,
  },{
    path:      '/verify-account/:token',
    component: VerifyAccount,
  },
];

/**
 * If a route has optional parameters, multiple lines for this route have to be
 * added here. The most specific one has to go on top.
 *
 * For example:
 *
 * {
 *   path: '/editor/:id/:view',
 *   component: Editor,
 * },{
 *   path: '/editor/:id',
 *   component: Editor,
 * }
 *
 */
export const privateRoutes: (RouteProps & {component: React.FunctionComponent})[] = [
  {
    path:      '/course/:id',   // Course view for a participant who is taking the course
    component: CoursePage,
  },{
    path:      '/program/:id',   // List of courses within that program for a participant
    component: Library,
  },{
    path:      '/package/:id',   // List of programs within that package for a participant
    component: Library,
  },{
    path:      '/certificates',
    component: Certificates,
  },{
    path:      '/courses/:id',   // List of courses for the editor and manager. If _id is set the manager can assign users to that course.
    component: Courses,
  },{
    path:      '/courses',   // List of courses for the editor and manager
    component: Courses,
  },{
    path:      '/evaluations',
    component: Evaluations,
  },{
    path:      '/programs/:id',
    component: Programs,
  },{
    path:      '/programs',
    component: Programs,
  },{
    path:      '/packages/:id',   // List of packages for the editor. If :id is set, the editor can edit that package
    component: Packages,
  },{
    path:      '/packages',   // List of packages for the editor. If :id is set, the editor can edit that package
    component: Packages,
  },{
    path:      '/editor/:id/:view',
    component: Editor,
  },{
    path:      '/editor/:id',
    component: Editor,
  },{
    path:      '/library/:id',
    component: Library,
  },{
    path:      '/library',
    component: Library,
  },{
    path:      '/settings',
    component: Settings,
  },{
    path:      '/analytics',
    component: Statistics,
  },{
    path:      '/licenses',
    component: Clients,
  },{
    path:      '/all-users/:id',
    component: Users,
  },{
    path:      '/all-users',
    component: Users,
  },{
    path:      '/faq',
    component: Faq,
  },{
    path:      '/customers/:id/:view/:subid',
    component: Customers,
  },{
    path:      '/customers/:id/:view/',
    component: Customers,
  },{
    path:      '/customers/:id',
    component: Customers,
  },{
    path:      '/customers',
    component: Customers,
  },{
    path:      '/system',
    component: SystemUsers,
  },{
    path:      '/email',
    component: Email,
  },
];

export const Routes = inject('routingStore')(observer((props: {routingStore?: RoutingStore}): JSX.Element => {
  const navigate: NavigateFunction = useNavigate();
  const location: Location         = useLocation();
  props.routingStore.setNavigate(navigate);

  React.useEffect(() => routingStore.setLocation(location), [location]);
  
  return (
    <ReactRoutes>
      {publicRoutes.map((r) => (
        <Route element={React.createElement(r.component)} path={r.path} key={r.path} />
      ))}
      {privateRoutes.map((r) => (
        <Route 
          key     = {r.path}
          path    = {r.path}
          element = {
            <PrivateRoute redirectTo = {`/${RoutingStore.loginPage}`} component={r.component} />
          }
        />
      ))}
      <Route element={<h1>404: page not found</h1>} />
    </ReactRoutes>
  )
}));

export const PrivateRoute = inject('authStore')(observer((props: any): JSX.Element | null => {
  const {
    redirectTo,
    authStore,
    component,
  } = props;

  return (
    authStore.isAuthenticating ? null : 
      authStore.isLoggedIn ? (
          <PageContainer>
              {React.createElement(component)}
          </PageContainer>
      ) : ( // Not logged in
      <Navigate  to={redirectTo || '/'} />
    )
  );
}));